import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Alert, Button, InputNumber, Typography } from "antd";

import { ancanaApi } from "../../../api/ancanaApi";
import { ISlideAppreciation } from "../../../types/models/presentations";

/**
 * The destination's estimated annual appreciation, edited where the brochure
 * prints it (Mazul v10's band, "4 – 6%"). Two numbers, so they are edited in
 * place rather than in the destination's own drawer.
 *
 * The range belongs to the destination, not the property: saving changes the
 * band of every property there, and the editor says so. Leaving both empty
 * leaves the band out of the brochure.
 */

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

export interface AppreciationEditorProps {
  destination: ISlideAppreciation;
  onSaved?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

const asNumber = (value: string | null) => (value === null ? null : Number(value));

/** antd's InputNumber reports a string or number, and '' or null once cleared. */
const fromInput = (value: string | number | null | undefined) =>
  value === null || value === undefined || value === "" ? null : Number(value);

const AppreciationEditor = ({ destination, onSaved, onDirtyChange }: AppreciationEditorProps) => {
  const [low, setLow] = useState<number | null>(asNumber(destination.low));
  const [high, setHigh] = useState<number | null>(asNumber(destination.high));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();

  // A fresh outline after a save brings the stored values back in.
  useEffect(() => {
    setLow(asNumber(destination.low));
    setHigh(asNumber(destination.high));
  }, [destination.low, destination.high]);

  const dirty = low !== asNumber(destination.low) || high !== asNumber(destination.high);
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);

  const inverted = low !== null && high !== null && low > high;

  const save = async () => {
    setSaving(true);
    setError(undefined);
    try {
      await ancanaApi.patch(`destinations/${destination.slug}/`, {
        appreciation_low: low, appreciation_high: high,
      });
      onDirtyChange?.(false);
      onSaved?.();
    } catch (e: any) {
      const errors = e?.response?.data;
      setError(errors?.appreciation_high?.[0] ?? errors?.appreciation_low?.[0]
               ?? e?.message ?? "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const others = destination.listings - 1;
  return (
    <>
      {error && <Alert type="error" message={error} style={{ marginBottom: 12 }} />}
      <Row>
        <InputNumber value={low ?? ""} onChange={(v) => setLow(fromInput(v))} min={0} max={99.9}
                     step={0.5} placeholder="Low" aria-label="Low end" />
        <span>–</span>
        <InputNumber value={high ?? ""} onChange={(v) => setHigh(fromInput(v))} min={0} max={99.9}
                     step={0.5} placeholder="High" aria-label="High end" />
        <span>% a year</span>
        <Button type="primary" onClick={save} loading={saving} disabled={!dirty || inverted}>
          Save
        </Button>
      </Row>
      <Typography.Text type={inverted ? "danger" : "secondary"} style={{ fontSize: 12 }}>
        {inverted
          ? "The low end is above the high end."
          : `${destination.name}'s range${others > 0
              ? `, shared with ${others} other ${others === 1 ? "property" : "properties"} there`
              : ""}. Leave both empty to leave the band out.`}
      </Typography.Text>
    </>
  );
};

export default AppreciationEditor;
